import { dialog } from '@forest/shared/solid'
import { createEffect, on, type Accessor } from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import { closeShop } from '~/lib/close-shop'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { RefreshTicket } from '~/services/refresh-tickets'
import type { DailyPicksRefreshFlow } from '~/store/daily-picks-refresh-flow'
import type { ShopBalances } from '~/store/session-ledger'
import {
  PurchaseBalanceOverlay,
  SHOP_ALERT_DIALOG,
  shopErrorDescription,
} from './shop-purchase-dialog'

const numberFormatter = new Intl.NumberFormat(intlLocale)

const confirmationDescription = (ticket: RefreshTicket) => {
  const values = { price: numberFormatter.format(ticket.price) }
  return ticket.currency === 'coin'
    ? m.refresh_with_coins(values)
    : m.refresh_with_time_crystals(values)
}

// The last-minute refresh lock's dialog (GLOSSARY.md): its placeholder names
// the tier's currency, not its price — what the lock saves is a spend in
// that currency, whatever this tier happens to cost.
const lockDescription = (ticket: RefreshTicket) =>
  m.dialog_refresh_in_one_min_content({
    price: ticket.currency === 'coin' ? m.coin() : m.time_crystal(),
  })

// The Daily Picks refresh's five dialogs (GLOSSARY.md "Daily Picks
// refresh"), all on the shared Solid dialog: the Confirm Refresh
// confirmation, with the balance pills over it like every other soft
// purchase, the one-minute dialog the last-minute lock answers a tap with,
// the Nothing-new dialog the exhausted pool answers one with, and the two
// error dialogs that end the refresh. There is no
// locked-confirmation phase — accepting dismisses the dialog at once and
// the refresh continues inline behind it (ADR 0032) — and no error dialog
// offers a retry: both close Shop, so the next open reads the server's
// assortment, balance, and refresh count. A balance shortage opens nothing
// at all; it navigates to Coins & Crystals.
export function DailyPicksRefreshDialogs(props: {
  balances: Accessor<ShopBalances | undefined>
  flow: DailyPicksRefreshFlow
}) {
  createEffect(
    on(
      () => props.flow.phase(),
      (phase) => {
        switch (phase.status) {
          case 'confirming': {
            // Read out here, inside the effect: the callback below runs long
            // after this tracked scope has ended.
            const { dismiss } = props.flow
            void dialog
              .open({
                ...SHOP_ALERT_DIALOG,
                action: props.flow.confirm,
                button: (
                  <>
                    <img
                      src={
                        phase.ticket.currency === 'coin' ? coinIcon : gemIcon
                      }
                      alt=""
                      class="mr-1 size-4"
                    />
                    {numberFormatter.format(phase.ticket.price)}
                  </>
                ),
                buttonTestId: 'daily-picks-refresh-confirm',
                description: confirmationDescription(phase.ticket),
                overlayContent: (
                  <PurchaseBalanceOverlay
                    balances={props.balances}
                    testIdPrefix="daily-picks-refresh-"
                  />
                ),
                secondaryButton: {
                  action: props.flow.cancel,
                  button: m.cancel(),
                  testId: 'daily-picks-refresh-cancel',
                },
                testId: 'daily-picks-refresh-confirmation',
                title: m.dialog_confirm_refresh_title(),
              })
              // The dialog's promise settles when it leaves the screen,
              // however it left: a confirmation closed by something other
              // than its own buttons still has to release the flow.
              .then(() => dismiss(phase))
            return
          }
          case 'exhausted': {
            const { dismiss } = props.flow
            void dialog
              .open({
                ...SHOP_ALERT_DIALOG,
                button: m.ok(),
                buttonTestId: 'daily-picks-refresh-exhausted-dismiss',
                description: m.dialog_nothing_new_content(),
                testId: 'daily-picks-refresh-exhausted',
                title: m.dialog_nothing_new_title(),
              })
              // As with the lock: OK only closes, so the flow is released
              // when the dialog leaves the screen.
              .then(() => dismiss(phase))
            return
          }
          case 'locked': {
            const { dismiss } = props.flow
            void dialog
              .open({
                ...SHOP_ALERT_DIALOG,
                button: m.ok(),
                buttonTestId: 'daily-picks-refresh-locked-dismiss',
                description: lockDescription(phase.ticket),
                testId: 'daily-picks-refresh-locked',
                title: m.dialog_refresh_in_one_min_title(),
              })
              // OK has nothing to do but close, so the flow is released
              // where every other dismissal releases it: when the dialog
              // leaves the screen.
              .then(() => dismiss(phase))
            return
          }
          case 'connection':
            void dialog.closeThenOpen({
              ...SHOP_ALERT_DIALOG,
              action: closeShop,
              button: m.ok(),
              buttonTestId: 'daily-picks-refresh-connection-close',
              description: m.dialog_connection_issue_description(),
              testId: 'daily-picks-refresh-connection',
              title: m.dialog_connection_issue_title(),
            })
            return
          case 'error':
            void dialog.closeThenOpen({
              ...SHOP_ALERT_DIALOG,
              action: closeShop,
              button: m.btn_got_it(),
              buttonTestId: 'daily-picks-refresh-error-dismiss',
              description: shopErrorDescription(phase.code),
              testId: 'daily-picks-refresh-error',
              title: m.dialog_something_went_wrong_title(),
            })
        }
      },
    ),
  )
  return null
}
